import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiArrowRight, FiArrowUpRight, FiCheck, FiShield, FiLock, FiGlobe,
  FiHeart, FiKey, FiStar, FiMenu, FiX, FiPlay, FiChevronDown,
  FiDatabase, FiCpu, FiPenTool, FiGitBranch, FiBarChart2,
  FiFileText, FiEdit3, FiCheckCircle, FiSend, FiRefreshCw, FiZap,
  FiBold, FiItalic, FiUnderline, FiList, FiAlignLeft, FiImage,
  FiMessageSquare,
} from 'react-icons/fi';
import './Landing.scss';

/* ══════════════════════════════════════════════════════════════════
   BRAND + CONTENT
   ══════════════════════════════════════════════════════════════════ */

const BRAND = {
  name: 'Nacsoft Contracts',
  short: 'Contracts',
  logo: 'https://nacsoft.com.tr/wp-content/uploads/2021/08/Nacsoft-Logo-Renkli@2x.png',
  tagline: 'The enterprise contract platform for teams that refuse to choose between speed and rigor.',
};

const NAV_LINKS = [
  { label: 'Platform',   href: '#capabilities' },
  { label: 'Industries', href: '#industries' },
  { label: 'Security',   href: '#tenancy' },
  { label: 'Pricing',    href: '#pricing' },
  { label: 'Customers',  href: '#proof' },
];

const COMPLIANCE = [
  { name: 'SOC 2 Type II', icon: FiShield },
  { name: 'ISO 27001',     icon: FiLock },
  { name: 'GDPR',          icon: FiGlobe },
  { name: 'HIPAA',         icon: FiHeart },
  { name: 'CCPA',          icon: FiKey },
];

const HERO_STATS = [
  { k: 'Contract volume managed',  v: '$180B+' },
  { k: 'Median cycle time',        v: '3.4 days' },
  { k: 'Tenants in production',    v: '2,148' },
];

const INDUSTRIES = [
  {
    id: 'finance',
    label: 'Financial Services',
    title: 'Underwrite faster, without loosening controls.',
    body: 'Run thousands of counterparty agreements across dedicated tenant databases — with signing keys, retention policies, and residency isolated by desk.',
    metrics: [
      { v: '68%',   k: 'Faster counterparty onboarding' },
      { v: '99.99%',k: 'Tenant-scoped uptime SLA' },
      { v: '< 4d',  k: 'Median ISDA turnaround' },
    ],
    bullets: ['Per-desk workspace isolation', 'BYO KMS + customer-managed keys', 'Regional residency (EU · UK · US · APAC)'],
  },
  {
    id: 'healthcare',
    label: 'Healthcare',
    title: 'PHI-grade rigor. Clinic-grade speed.',
    body: 'HIPAA-aligned workflows for payer agreements, BAAs, and clinical research contracts — with immutable audit trails every regulator will recognize.',
    metrics: [
      { v: '100%', k: 'BAA coverage across networks' },
      { v: '3.1d', k: 'Average BAA cycle time' },
      { v: '14×',  k: 'Clinical site onboarding lift' },
    ],
    bullets: ['HIPAA + HITRUST aligned controls', 'Clinical site provisioning workflows', 'PHI-scoped role-based access'],
  },
  {
    id: 'saas',
    label: 'SaaS & Tech',
    title: 'MSAs that close at sales speed.',
    body: 'Let revenue self-serve compliant MSAs, order forms, and DPAs — with legal guardrails, CRM-native workflows, and live deal-desk telemetry.',
    metrics: [
      { v: '4.2×', k: 'Revenue self-serve lift' },
      { v: '92%',  k: 'Clause-library adoption' },
      { v: '-58%', k: 'Legal ticket volume' },
    ],
    bullets: ['Salesforce + HubSpot-native routing', 'Guided redline automation', 'Deal-desk cycle-time dashboards'],
  },
  {
    id: 'legal',
    label: 'Legal Services',
    title: 'A single system of record for every matter.',
    body: 'Standardize playbooks across offices, capture every negotiation artefact, and deliver matter-level attribution partners actually trust.',
    metrics: [
      { v: '12K+', k: 'Matters under management' },
      { v: '100%', k: 'Matter-level audit coverage' },
      { v: '40+',  k: 'Jurisdictions supported' },
    ],
    bullets: ['Clause library with inheritance', 'Matter-level audit trails', 'Conflict-of-interest guardrails'],
  },
  {
    id: 'manufacturing',
    label: 'Manufacturing',
    title: 'Every vendor. Every obligation. One ledger.',
    body: 'Track renewals, enforce spend thresholds, and surface counterparty risk before it hits the balance sheet — across global supply operations.',
    metrics: [
      { v: '$1.2B', k: 'Spend commitments tracked' },
      { v: '-42%',  k: 'Renewal cliff surprises' },
      { v: '28',    k: 'Plant-level tenants live' },
    ],
    bullets: ['Renewal + cliff alerts', 'Vendor risk scoring', 'Plant-level workspace provisioning'],
  },
];

const CAPABILITIES = [
  { key: 'ai',        span: 'xl',   icon: FiCpu,       badge: 'AI Drafting',  title: 'Clauses that stand up in court — drafted in seconds.',                body: 'Train on your playbook. Draft from a brief. Every output is cited, traceable, and editable by your team.' },
  { key: 'sign',      span: 'tall', icon: FiPenTool,   badge: 'E-signature',  title: 'Native e-signature, legally binding in 40+ jurisdictions.',           body: 'No third-party fees. Cryptographic proof of intent. Signed contracts lock the moment the pen lifts.' },
  { key: 'flow',      span: 'sm',   icon: FiGitBranch, badge: 'Workflow',     title: 'Conditional approvals, no-code.',                                     body: 'Route by value, region, or risk — with every deviation logged.' },
  { key: 'audit',     span: 'sm',   icon: FiLock,      badge: 'Audit',        title: 'Immutable audit trail.',                                              body: 'Every view, edit, signature, and export captured with tamper-evident hashing.' },
  { key: 'analytics', span: 'wide', icon: FiBarChart2, badge: 'Analytics',    title: 'Cycle time you can actually shorten.',                                body: 'Measure time-in-stage, bottleneck owners, and negotiation heatmaps across every template.' },
];

const WORKFLOW = [
  { key: 'intake',  icon: FiZap,         label: 'Intake',  brief: 'Self-serve requests routed instantly.',       stat: '< 2 min' },
  { key: 'draft',   icon: FiFileText,    label: 'Draft',   brief: 'AI pulls from your playbook with citations.', stat: '45 sec' },
  { key: 'review',  icon: FiEdit3,       label: 'Review',  brief: 'Parallel redlines across teams.',             stat: '1.4 days' },
  { key: 'approve', icon: FiCheckCircle, label: 'Approve', brief: 'Conditional routing by value and risk.',      stat: '6 hours' },
  { key: 'sign',    icon: FiSend,        label: 'Sign',    brief: 'Native e-signature — all parties locked.',    stat: '12 min' },
  { key: 'renew',   icon: FiRefreshCw,   label: 'Renew',   brief: 'Renewal cliffs surfaced 120 days out.',       stat: 'On rails' },
];

const TENANCY_POINTS = [
  { icon: FiDatabase, title: 'Database-per-tenant isolation',   body: 'Each customer gets a dedicated MongoDB cluster — queries can’t cross tenant boundaries, even if they tried.' },
  { icon: FiLock,     title: 'Workspace-scoped encryption',     body: 'Envelope encryption with per-tenant DEKs, rotated automatically or on demand.' },
  { icon: FiGlobe,    title: 'Regional data residency',         body: 'Pin workspaces to EU, UK, US, or APAC — with auditable, enforced locality.' },
  { icon: FiKey,      title: 'BYO identity & keys',             body: 'SAML SSO, SCIM provisioning, and customer-managed KMS keys at every tier.' },
];

const METRICS = [
  { value: 92,    suffix: '%', label: 'Faster contract turnaround',        accent: 'brand' },
  { value: 3.4,   suffix: 'd', label: 'Average end-to-end cycle time',     accent: 'ink' },
  { value: 99.99, suffix: '%', label: 'Multi-tenant uptime SLA',           accent: 'cyan' },
  { value: 12000, suffix: '+', label: 'Contracts processed every month',   accent: 'brand' },
];

const TESTIMONIALS = [
  { quote: 'We replaced three tools with one platform and cut our deal-desk turnaround from fourteen days to under four. The board noticed.', name: 'Alex Morgan',   role: 'VP Legal Operations', org: 'Acme Industries',   avatar: 'AM' },
  { quote: 'Tenant isolation was non-negotiable for us. This platform passed our infosec review on the first pass — I have never said that about a vendor before.', name: 'Jordan Chen',   role: 'CISO',                org: 'Globex Holdings',   avatar: 'JC' },
  { quote: 'The audit trail alone saved us from a nightmare renewal season. Legal, finance, and procurement finally share one system of record.', name: 'Taylor Blake',  role: 'General Counsel',     org: 'Initech Systems',   avatar: 'TB' },
];

const PLANS = [
  { name: 'Team',       price: '$29',    period: 'per user / month',  tagline: 'For lean legal and ops teams getting started.',       cta: 'Start free trial',
    features: ['Up to 25 seats', 'Unlimited contracts', 'Native e-signature', 'Clause library', 'SSO (Google, Microsoft)', 'Standard support'] },
  { name: 'Business',   price: '$79',    period: 'per user / month',  tagline: 'For growing companies with formal review workflows.', cta: 'Start free trial', popular: true,
    features: ['Everything in Team', 'Approval workflows', 'AI clause drafting', 'Salesforce & HubSpot sync', 'Custom retention policies', 'Priority support · 99.9% SLA'] },
  { name: 'Enterprise', price: 'Custom', period: 'contact sales',     tagline: 'For regulated industries and global deployments.',    cta: 'Contact sales',
    features: ['Everything in Business', 'Database-per-tenant isolation', 'SAML SSO & SCIM provisioning', 'Dedicated VPC deployment', 'Custom DPA & BAA', 'Named CSM · 99.99% SLA'] },
];

const FAQ_ITEMS = [
  { q: 'How is tenant isolation actually enforced?',          a: 'Every workspace runs against its own dedicated MongoDB database with tenant-scoped credentials. Requests are resolved via signed subdomain claims, and no shared collection is reachable by any other tenant at any layer — not API, not worker, not analytics.' },
  { q: 'Can we deploy into our own cloud?',                   a: 'Yes. Enterprise customers can deploy Nacsoft into a dedicated VPC on AWS, Azure, or GCP — with private networking, customer-managed KMS keys, and regional data residency (EU, UK, US, APAC).' },
  { q: 'Do you support our existing e-signature vendor?',     a: 'Native e-signature is included at every tier. For teams standardized on DocuSign, Adobe Sign, or Dropbox Sign, we offer certified integrations that keep audit trails in sync.' },
  { q: 'How does the AI drafting handle confidentiality?',    a: 'AI inference runs on dedicated endpoints with zero data retention. Your playbooks, clauses, and document content are never used to train shared models — and Enterprise customers can bring their own model endpoints.' },
  { q: 'What happens to our data if we leave?',               a: 'You own your data, always. Export the full workspace — contracts, audit trail, attachments, and metadata — as a versioned ZIP at any time. We retain it for 30 days after cancellation, then permanently delete.' },
  { q: 'How fast can we go live?',                            a: 'Most teams are productive in under a week. Import existing templates, configure SSO, and connect your CRM — our onboarding team handles playbook migration and runs dry-run cycles with you.' },
];

/* ══════════════════════════════════════════════════════════════════
   HOOKS
   ══════════════════════════════════════════════════════════════════ */

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
}

function useScrollState() {
  const [state, setState] = useState({ scrolled: false, hidden: false, progress: 0 });
  const last = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(y / max, 1) : 0;
      const delta = y - last.current;
      setState((s) => ({
        scrolled: y > 24,
        hidden: y > 320 && delta > 8 ? true : (delta < -4 || y < 120 ? false : s.hidden),
        progress,
      }));
      last.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return state;
}

function useReveal() {
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll('.nx-reveal'));
    nodes.forEach((node) => {
      const parent = node.closest('[data-stagger]') || node.parentElement;
      if (parent) {
        const sibs = Array.from(parent.querySelectorAll('.nx-reveal'));
        const i = sibs.indexOf(node);
        if (i >= 0) node.style.setProperty('--nx-i', i);
      }
    });
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -72px 0px' }
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
}

function useInView(ref, { once = true, threshold = 0.3 } = {}) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          setInView(true);
          if (once) io.unobserve(e.target);
        } else if (!once) setInView(false);
      }),
      { threshold }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, once, threshold]);
  return inView;
}

function useCountUp(end, trigger, { duration = 1600, decimals = 0 } = {}) {
  const [value, setValue] = useState(0);
  const reduced = useReducedMotion();
  useEffect(() => {
    if (!trigger) return undefined;
    if (reduced) { setValue(end); return undefined; }
    let raf = 0;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(end * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end, trigger, duration, reduced]);
  return decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString();
}

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════ */

function Landing() {
  const navigate = useNavigate();
  const { scrolled, hidden, progress } = useScrollState();
  const [mobileOpen, setMobileOpen] = useState(false);
  useReveal();

  const goLogin = useCallback(() => navigate('/login'), [navigate]);
  const goContact = useCallback(() => navigate('/login'), [navigate]);

  return (
    <div className="nx">
      <div className="nx-progress" style={{ transform: `scaleX(${progress})` }} aria-hidden="true" />

      <Nav
        scrolled={scrolled}
        hidden={hidden && !mobileOpen}
        mobileOpen={mobileOpen}
        onToggleMobile={() => setMobileOpen((v) => !v)}
        onLogin={goLogin}
        onContact={goContact}
      />

      <Hero onLogin={goLogin} onContact={goContact} />
      <Industries />
      <Capabilities />
      <Workflow />
      <Tenancy />
      <Metrics />
      <Testimonials />
      <Pricing onLogin={goLogin} onContact={goContact} />
      <Faq />
      <FinalCTA onLogin={goLogin} onContact={goContact} />
      <Footer />
    </div>
  );
}

export default Landing;

/* ══════════════════════════════════════════════════════════════════
   NAV
   ══════════════════════════════════════════════════════════════════ */

function Nav({ scrolled, hidden, mobileOpen, onToggleMobile, onLogin, onContact }) {
  return (
    <header
      className={[
        'nx-nav',
        scrolled ? 'is-scrolled' : '',
        hidden ? 'is-hidden' : '',
        mobileOpen ? 'is-mobile-open' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="nx-nav__inner">
        <Link to="/" className="nx-brand">
          <img src={BRAND.logo} alt={BRAND.name} />
          <span className="nx-brand__divider" aria-hidden="true" />
          <span className="nx-brand__name">{BRAND.short}</span>
        </Link>

        <nav className="nx-nav__links" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href}>{l.label}</a>
          ))}
        </nav>

        <div className="nx-nav__cta">
          <button type="button" className="nx-btn nx-btn--ghost nx-btn--sm" onClick={onLogin}>Sign in</button>
          <button type="button" className="nx-btn nx-btn--primary nx-btn--sm" onClick={onContact}>
            Contact sales <FiArrowRight />
          </button>
        </div>

        <button
          type="button"
          className="nx-nav__menu"
          onClick={onToggleMobile}
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>

      <div className="nx-nav__drawer" role="dialog" aria-hidden={!mobileOpen}>
        {NAV_LINKS.map((l) => (
          <a key={l.href} href={l.href} onClick={onToggleMobile}>
            {l.label} <FiArrowUpRight />
          </a>
        ))}
        <div className="nx-nav__drawer-cta">
          <button type="button" className="nx-btn nx-btn--outline nx-btn--full" onClick={onLogin}>Sign in</button>
          <button type="button" className="nx-btn nx-btn--primary nx-btn--full" onClick={onContact}>
            Contact sales <FiArrowRight />
          </button>
        </div>
      </div>
    </header>
  );
}

/* ══════════════════════════════════════════════════════════════════
   HERO
   ══════════════════════════════════════════════════════════════════ */

function Hero({ onLogin, onContact }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    let raf = 0;
    const onMove = (e) => {
      const { clientX: x, clientY: y } = e;
      const { innerWidth: w, innerHeight: h } = window;
      const mx = (x / w - 0.5) * 2;
      const my = (y / h - 0.5) * 2;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty('--mx', mx.toFixed(3));
        el.style.setProperty('--my', my.toFixed(3));
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
    };
  }, [reduced]);

  return (
    <section className="nx-hero" ref={ref}>
      <div className="nx-hero__canvas" aria-hidden="true" />
      <div className="nx-hero__grid" aria-hidden="true" />

      <div className="nx-container nx-container--wide">
        <div className="nx-hero__split">
          <div className="nx-hero__copy" data-stagger>
            <div className="nx-eyebrow nx-reveal">
              <span className="nx-eyebrow__dot" />
              Enterprise Contract Intelligence · Multi-tenant
            </div>

            <h1 className="nx-hero__title nx-reveal">
              Close every contract at the <em>speed of trust.</em>
            </h1>

            <p className="nx-hero__sub nx-reveal">
              One secure workspace for drafting, negotiating, signing, and renewing every
              agreement across your organization — with database-per-tenant isolation,
              auditable to the millisecond, ready for the Fortune 500.
            </p>

            <div className="nx-hero__cta nx-reveal">
              <button type="button" className="nx-btn nx-btn--primary nx-btn--lg" onClick={onContact}>
                Contact sales <FiArrowRight />
              </button>
              <button type="button" className="nx-btn nx-btn--outline nx-btn--lg" onClick={onLogin}>
                <FiPlay /> Watch 90-sec tour
              </button>
            </div>

            <ul className="nx-hero__inline-proof nx-reveal">
              <li><FiCheck /> 2,000+ legal teams</li>
              <li><FiCheck /> SOC 2 Type II</li>
              <li><FiCheck /> ISO 27001</li>
            </ul>

            <div className="nx-hero__stats" data-stagger>
              {HERO_STATS.map((s) => (
                <div key={s.k} className="nx-hero__stats-item nx-reveal">
                  <div className="nx-hero__stats-v">{s.v}</div>
                  <div className="nx-hero__stats-k">{s.k}</div>
                </div>
              ))}
            </div>
          </div>

          <HeroPanel />
        </div>
      </div>
    </section>
  );
}

function HeroPanel() {
  return (
    <div className="nx-hero__panel nx-reveal">
      <div className="nx-doc">
        <div className="nx-doc__chrome">
          <div className="nx-doc__title">
            <FiFileText />
            <span>Master Services Agreement</span>
            <em>v4.2 · shared</em>
          </div>
          <div className="nx-doc__users">
            <span className="nx-doc__user nx-doc__user--a" title="Sarah Jenkins">SJ</span>
            <span className="nx-doc__user nx-doc__user--b" title="Daniel Okonkwo">DO</span>
            <span className="nx-doc__user nx-doc__user--more">+3</span>
          </div>
        </div>

        <div className="nx-doc__toolbar" aria-hidden="true">
          <button type="button"><FiBold /></button>
          <button type="button"><FiItalic /></button>
          <button type="button"><FiUnderline /></button>
          <span className="nx-doc__sep" />
          <button type="button"><FiAlignLeft /></button>
          <button type="button"><FiList /></button>
          <span className="nx-doc__sep" />
          <button type="button"><FiImage /></button>
          <button type="button"><FiMessageSquare /></button>
          <span className="nx-doc__toolbar-spacer" />
          <span className="nx-doc__savebadge"><i /> Auto-saved</span>
        </div>

        <div className="nx-doc__page">
          <div className="nx-doc__heading">Master Services Agreement</div>
          <div className="nx-doc__subheading">
            Between <b>Lumen AI</b> (“Provider”) and <b>Vantage Labs</b> (“Customer”)
          </div>

          <div className="nx-doc__section">§ 4 · Service Level Commitments</div>
          <p className="nx-doc__p">
            The Provider agrees to maintain{' '}
            <span className="nx-doc__typed nx-doc__typed--a">
              99.99% monthly uptime across all production endpoints
              <span className="nx-doc__cursor nx-doc__cursor--a">
                <span className="nx-doc__flag">Sarah J</span>
                <span className="nx-doc__caret" />
              </span>
            </span>
            , excluding scheduled maintenance windows not to exceed four hours per calendar
            quarter, with service credits as defined in Exhibit B.
          </p>

          <div className="nx-doc__section">§ 8 · Limitation of Liability</div>
          <p className="nx-doc__p">
            In no event shall either party be liable for indirect, incidental,{' '}
            <span className="nx-doc__typed nx-doc__typed--b">
              or consequential damages exceeding twelve months of fees paid
              <span className="nx-doc__cursor nx-doc__cursor--b">
                <span className="nx-doc__flag">Daniel O</span>
                <span className="nx-doc__caret" />
              </span>
            </span>
            , regardless of the theory of liability or whether such party was advised of
            the possibility of such damages.
          </p>
        </div>

        <div className="nx-doc__statusbar">
          <span className="nx-doc__status-left">
            <i className="nx-doc__dot" /> 2 editors · live
          </span>
          <span className="nx-doc__status-right">
            Page 1 of 12 · 3,418 words
          </span>
        </div>
      </div>

      <div className="nx-hero__chip nx-hero__chip--a">
        <i /> Live · 2 editors
      </div>
      <div className="nx-hero__chip nx-hero__chip--b">
        <FiCpu /> AI clause suggestion ready
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   INDUSTRIES (tabbed)
   ══════════════════════════════════════════════════════════════════ */

function Industries() {
  const [active, setActive] = useState(INDUSTRIES[0].id);
  const current = INDUSTRIES.find((i) => i.id === active) || INDUSTRIES[0];

  return (
    <section id="industries" className="nx-section">
      <div className="nx-container">
        <SectionHead
          eyebrow="Industries"
          title={<>Built for the verticals that <em>live</em> inside contracts.</>}
          sub="One platform, calibrated to the risk profiles, workflows, and regulators of your industry. Switch between roles to see how each team works inside Nacsoft."
        />
        <div className="nx-ind__wrap nx-reveal">
          <div className="nx-ind__tabs" role="tablist">
            {INDUSTRIES.map((ind) => (
              <button
                key={ind.id}
                role="tab"
                aria-selected={ind.id === active}
                className={`nx-ind__tab ${ind.id === active ? 'is-active' : ''}`}
                onClick={() => setActive(ind.id)}
              >
                {ind.label}
              </button>
            ))}
          </div>
          <div className="nx-ind__body" key={active}>
            <div className="nx-ind__copy">
              <h3 className="nx-ind__title">{current.title}</h3>
              <p className="nx-ind__desc">{current.body}</p>
              <ul className="nx-ind__bullets">
                {current.bullets.map((b) => (
                  <li key={b}><FiCheck /> {b}</li>
                ))}
              </ul>
            </div>
            <div className="nx-ind__metrics">
              {current.metrics.map((m) => (
                <div key={m.k} className="nx-ind__metric">
                  <div className="nx-ind__metric-v">{m.v}</div>
                  <div className="nx-ind__metric-k">{m.k}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
   CAPABILITIES (bento)
   ══════════════════════════════════════════════════════════════════ */

function Capabilities() {
  return (
    <section id="capabilities" className="nx-section nx-section--alt">
      <div className="nx-container">
        <SectionHead
          eyebrow="Platform"
          title={<>One surface. <em>Every</em> contract workflow.</>}
          sub="From first draft to final renewal — every tool your contract touches, unified under one calm cursor."
        />
        <div className="nx-bento" data-stagger>
          {CAPABILITIES.map((f) => <BentoCard key={f.key} feature={f} />)}
        </div>
      </div>
    </section>
  );
}

function BentoCard({ feature }) {
  const Icon = feature.icon;
  const ref = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const bx = (e.clientX - rect.left) / rect.width;
      const by = (e.clientY - rect.top) / rect.height;
      el.style.setProperty('--bx', bx.toFixed(3));
      el.style.setProperty('--by', by.toFixed(3));
    };
    const onLeave = () => {
      el.style.setProperty('--bx', '0.5');
      el.style.setProperty('--by', '0.5');
    };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [reduced]);

  return (
    <article ref={ref} className={`nx-bento__card nx-bento__card--${feature.span} nx-reveal`}>
      <div className="nx-bento__badge"><Icon /> {feature.badge}</div>
      <h3 className="nx-bento__title">{feature.title}</h3>
      <p className="nx-bento__body">{feature.body}</p>
      <div className="nx-bento__visual">
        <BentoVisual k={feature.key} />
      </div>
    </article>
  );
}

function BentoVisual({ k }) {
  if (k === 'ai')        return <VisualAI />;
  if (k === 'sign')      return <VisualSign />;
  if (k === 'flow')      return <VisualFlow />;
  if (k === 'audit')     return <VisualAudit />;
  if (k === 'analytics') return <VisualAnalytics />;
  return null;
}

function VisualAI() {
  return (
    <div className="nx-vis-ai">
      <div className="nx-vis-ai__prompt">
        <span>›</span> Draft a 12-month SLA with 99.9% uptime
      </div>
      <div className="nx-vis-ai__out">
        The Provider agrees to maintain <mark>99.9% monthly uptime</mark>, excluding
        scheduled maintenance windows, for the duration of the twelve-month term.
        <span className="nx-vis-ai__caret" />
      </div>
      <div className="nx-vis-ai__chips">
        <span>Cited: Master SLA v3.2</span>
        <span>Risk: low</span>
      </div>
    </div>
  );
}

function VisualSign() {
  return (
    <div className="nx-vis-sign">
      <div className="nx-vis-sign__svg">
        <svg viewBox="0 0 220 60" fill="none" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <defs>
            <linearGradient id="nxSig" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"  stopColor="#FF8C03" />
              <stop offset="100%" stopColor="#C2570B" />
            </linearGradient>
          </defs>
          <path
            d="M5 42 C 30 6, 55 56, 85 24 S 135 52, 155 18 S 195 48, 215 32"
            stroke="url(#nxSig)"
            strokeWidth="2.6"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <div className="nx-vis-sign__meta">
        <div><FiCheck /> Signed · all parties</div>
        <div className="nx-vis-sign__meta-hash">hash · 0x8f2a…c91e</div>
      </div>
    </div>
  );
}

function VisualFlow() {
  return (
    <div className="nx-vis-flow">
      <div className="nx-vis-flow__step">Draft</div>
      <div className="nx-vis-flow__bar" />
      <div className="nx-vis-flow__step is-active">Review <em>3/4</em></div>
      <div className="nx-vis-flow__bar" />
      <div className="nx-vis-flow__step">Sign</div>
    </div>
  );
}

function VisualAudit() {
  const rows = ['Created · 09:14', 'Edited · 09:22', 'Reviewed · 11:02', 'Approved · 14:48', 'Signed · 15:30'];
  return (
    <div className="nx-vis-audit">
      {rows.map((row, i) => (
        <div key={row} className="nx-vis-audit__row" style={{ '--i': i }}>
          <i /> <span>{row}</span>
        </div>
      ))}
    </div>
  );
}

function VisualAnalytics() {
  const bars = [62, 78, 52, 86, 70, 94, 80];
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  return (
    <div className="nx-vis-analytics">
      <div className="nx-vis-analytics__bars">
        {bars.map((h, i) => (
          <span key={i} style={{ '--h': `${h}%`, '--i': i }} />
        ))}
      </div>
      <div className="nx-vis-analytics__axis">
        {days.map((d, i) => <span key={i}>{d}</span>)}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   WORKFLOW STEPPER
   ══════════════════════════════════════════════════════════════════ */

function Workflow() {
  return (
    <section className="nx-section">
      <div className="nx-container">
        <SectionHead
          eyebrow="End-to-end"
          title={<>From intake to renewal — <em>one rail.</em></>}
          sub="Every stage of the contract lifecycle instrumented, timed, and auditable — out of the box."
        />
        <div className="nx-flow nx-reveal" data-stagger>
          {WORKFLOW.map((step, idx) => {
            const Icon = step.icon;
            return (
              <div key={step.key} className={`nx-flow__step nx-reveal ${idx === 2 ? 'is-active' : ''}`}>
                <div className="nx-flow__step-icon"><Icon /></div>
                <div className="nx-flow__step-label">{step.label}</div>
                <div className="nx-flow__step-brief">{step.brief}</div>
                <div className="nx-flow__step-stat">{step.stat}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MULTI-TENANT SPOTLIGHT
   ══════════════════════════════════════════════════════════════════ */

function Tenancy() {
  return (
    <section id="tenancy" className="nx-tenancy">
      <div className="nx-container">
        <div className="nx-tenancy__grid">
          <div data-stagger>
            <div className="nx-eyebrow nx-reveal">
              <span className="nx-eyebrow__dot" />
              Multi-tenant architecture
            </div>
            <h2 className="nx-section-head__title nx-reveal" style={{ marginTop: 16 }}>
              One platform. <em>Unbreakable</em> boundaries.
            </h2>
            <p className="nx-tenancy__lede nx-reveal">
              Every workspace runs on a dedicated database with tenant-scoped credentials,
              independent retention policies, and its own signing keys. No shared collection.
              No leaky abstraction. No exceptions.
            </p>
            <ul className="nx-tenancy__list" data-stagger>
              {TENANCY_POINTS.map((p) => {
                const Icon = p.icon;
                return (
                  <li key={p.title} className="nx-reveal">
                    <div className="nx-tenancy__list-icon"><Icon /></div>
                    <div>
                      <strong>{p.title}</strong>
                      <span>{p.body}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <TenancyDiagram />
        </div>
      </div>
    </section>
  );
}

function TenancyDiagram() {
  const tenants = [
    { label: 'acme.nacsoft',    color: 'brand' },
    { label: 'lumen.nacsoft',   color: 'green' },
    { label: 'vantage.nacsoft', color: 'cyan' },
  ];
  return (
    <div className="nx-diag nx-reveal" aria-hidden="true">
      <div className="nx-diag__label">Nacsoft Platform</div>
      <div className="nx-diag__row">
        {tenants.map((t) => (
          <div key={t.label} className={`nx-diag__tenant nx-diag__tenant--${t.color}`}>
            <div className="nx-diag__tenant-head"><i /> {t.label}</div>
            <div className="nx-diag__tenant-row"><span /><span /></div>
            <div className="nx-diag__tenant-row"><span style={{ flex: '0 0 68%' }} /></div>
            <div className="nx-diag__tenant-db"><FiDatabase /> isolated DB</div>
          </div>
        ))}
      </div>
      <div className="nx-diag__layers">
        <span>Identity · SSO · RBAC</span>
        <span>Policy engine · audit bus</span>
        <span>Envelope encryption · KMS</span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   METRICS (dark inversion)
   ══════════════════════════════════════════════════════════════════ */

function Metrics() {
  const ref = useRef(null);
  const inView = useInView(ref);

  return (
    <section className="nx-metrics" ref={ref}>
      <div className="nx-container">
        <div className="nx-metrics__head">
          <div className="nx-eyebrow nx-eyebrow--light">
            <span className="nx-eyebrow__dot" /> Impact
          </div>
          <h2 className="nx-metrics__title">
            Contract velocity, <em>measured.</em>
          </h2>
        </div>
        <div className="nx-metrics__grid">
          {METRICS.map((m) => <MetricCard key={m.label} metric={m} trigger={inView} />)}
        </div>
      </div>
    </section>
  );
}

function MetricCard({ metric, trigger }) {
  const decimals = metric.value % 1 !== 0 ? (metric.value < 100 ? 2 : 0) : 0;
  const value = useCountUp(metric.value, trigger, { decimals });
  return (
    <div className={`nx-metric nx-metric--${metric.accent}`}>
      <div className="nx-metric__value">
        {value}<em>{metric.suffix}</em>
      </div>
      <div className="nx-metric__label">{metric.label}</div>
      <span className="nx-metric__pulse" />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   TESTIMONIALS
   ══════════════════════════════════════════════════════════════════ */

function Testimonials() {
  return (
    <section id="proof" className="nx-section">
      <div className="nx-container">
        <SectionHead
          eyebrow="Customer proof"
          title={<>Loved by the teams <em>closing the deals.</em></>}
          sub="From regulated financial institutions to fast-moving SaaS companies — Nacsoft is the contract system of record."
        />
        <div className="nx-quotes" data-stagger>
          {TESTIMONIALS.map((t, i) => (
            <figure key={i} className="nx-quote nx-reveal">
              <div className="nx-quote__stars">
                {[0, 1, 2, 3, 4].map((s) => <FiStar key={s} />)}
              </div>
              <blockquote className="nx-quote__body">“{t.quote}”</blockquote>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
   PRICING
   ══════════════════════════════════════════════════════════════════ */

function Pricing({ onLogin, onContact }) {
  return (
    <section id="pricing" className="nx-section nx-section--alt">
      <div className="nx-container">
        <SectionHead
          eyebrow="Pricing"
          title={<>Start free. <em>Scale deliberately.</em></>}
          sub="Transparent per-seat pricing. No per-contract fees, no surprise bills, no arbitrary caps."
        />
        <div className="nx-plans" data-stagger>
          {PLANS.map((p) => (
            <article key={p.name} className={`nx-plan nx-reveal ${p.popular ? 'is-popular' : ''}`}>
              {p.popular && <div className="nx-plan__badge">Most popular</div>}
              <div className="nx-plan__name">{p.name}</div>
              <div className="nx-plan__tagline">{p.tagline}</div>
              <div className="nx-plan__price">
                <span>{p.price}</span>
                <em>{p.period}</em>
              </div>
              <button
                type="button"
                className={`nx-btn nx-btn--full ${p.popular ? 'nx-btn--primary' : 'nx-btn--outline'}`}
                onClick={p.cta === 'Contact sales' ? onContact : onLogin}
              >
                {p.cta}
              </button>
              <ul className="nx-plan__features">
                {p.features.map((f) => (
                  <li key={f}><FiCheck /> {f}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
   FAQ
   ══════════════════════════════════════════════════════════════════ */

function Faq() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section id="faq" className="nx-section">
      <div className="nx-container nx-container--narrow">
        <SectionHead
          eyebrow="FAQ"
          title={<>Questions, <em>answered.</em></>}
          sub="Still curious? Our solutions team is a click away."
        />
        <div className="nx-faq">
          {FAQ_ITEMS.map((item, i) => (
            <FaqItem
              key={item.q}
              item={item}
              index={i}
              open={i === openIdx}
              onToggle={() => setOpenIdx(i === openIdx ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqItem({ item, index, open, onToggle }) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (revealed || !ref.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          setRevealed(true);
          io.unobserve(e.target);
        }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -72px 0px' }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [revealed]);

  return (
    <div
      ref={ref}
      className={[
        'nx-faq__item',
        'nx-reveal',
        revealed ? 'is-in' : '',
        open ? 'is-open' : '',
      ].filter(Boolean).join(' ')}
      style={{ '--nx-i': index }}
    >
      <button
        type="button"
        className="nx-faq__q"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span>{item.q}</span>
        <FiChevronDown />
      </button>
      <div className="nx-faq__a"><p>{item.a}</p></div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   FINAL CTA
   ══════════════════════════════════════════════════════════════════ */

function FinalCTA({ onLogin, onContact }) {
  return (
    <section className="nx-cta">
      <div className="nx-container">
        <div className="nx-cta__card nx-reveal">
          <div className="nx-eyebrow nx-eyebrow--light">
            <span className="nx-eyebrow__dot" /> Ready when you are
          </div>
          <h2 className="nx-cta__title">
            Unlock deal velocity — <em>without compromise.</em>
          </h2>
          <p className="nx-cta__sub">
            Walk through your current workflow with our solutions team. We’ll map a migration
            plan and run a pilot with your templates in under two weeks.
          </p>
          <div className="nx-cta__actions">
            <button type="button" className="nx-btn nx-btn--primary nx-btn--lg" onClick={onContact}>
              Contact sales <FiArrowRight />
            </button>
            <button type="button" className="nx-btn nx-btn--glass nx-btn--lg" onClick={onLogin}>
              Start free trial
            </button>
          </div>
          <ul className="nx-cta__meta">
            <li><FiCheck /> 14-day trial, no card</li>
            <li><FiCheck /> White-glove migration</li>
            <li><FiCheck /> Cancel any time</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
   FOOTER
   ══════════════════════════════════════════════════════════════════ */

function Footer() {
  return (
    <footer className="nx-footer">
      <div className="nx-container nx-container--wide">
        <div className="nx-footer__grid">
          <div className="nx-footer__brand">
            <img src={BRAND.logo} alt={BRAND.name} />
            <p className="nx-footer__tagline">{BRAND.tagline}</p>
            <div className="nx-footer__badges">
              {COMPLIANCE.slice(0, 3).map((b) => {
                const Icon = b.icon;
                return (
                  <span key={b.name} className="nx-footer__badge">
                    <Icon /> {b.name}
                  </span>
                );
              })}
            </div>
          </div>

          <FooterCol title="Platform"  items={['AI Drafting', 'E-signature', 'Workflows', 'Audit trail', 'Integrations']} />
          <FooterCol title="Solutions" items={['Legal Ops', 'Revenue', 'Procurement', 'People & HR', 'IT & Security']} />
          <FooterCol title="Resources" items={['Docs', 'API reference', 'Security center', 'Changelog', 'Status']} />
          <FooterCol title="Company"   items={['About', 'Customers', 'Careers', 'Contact']} />
        </div>

        <div className="nx-footer__bottom">
          <div>© {new Date().getFullYear()} {BRAND.name}. A Nacsoft product.</div>
          <div className="nx-footer__bottom-links">
            <a href="#privacy">Privacy</a>
            <a href="#terms">Terms</a>
            <a href="#dpa">DPA</a>
            <a href="#security">Security</a>
          </div>
          <div className="nx-footer__status">
            <i /> All systems operational
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  return (
    <div className="nx-footer__col">
      <div className="nx-footer__col-title">{title}</div>
      {items.map((i) => <a key={i} href="#">{i}</a>)}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   SHARED
   ══════════════════════════════════════════════════════════════════ */

function SectionHead({ eyebrow, title, sub }) {
  return (
    <header className="nx-section-head" data-stagger>
      <div className="nx-eyebrow nx-reveal">
        <span className="nx-eyebrow__dot" /> {eyebrow}
      </div>
      <h2 className="nx-section-head__title nx-reveal">{title}</h2>
      {sub && <p className="nx-section-head__sub nx-reveal">{sub}</p>}
    </header>
  );
}
